// Cameras.js - Câmaras TP-Link Tapo (snapshots RTSP)
//
// Não há stream contínuo: o backend tira uma fotografia com ffmpeg a cada
// pedido. O "modo live" é isso mesmo repetido de dois em dois segundos, e
// por isso é opt-in por câmara — deixá-lo sempre ligado em todas seria
// pedir ao servidor um ffmpeg por segundo sem ninguém a olhar.
//
// A vista busca a sua própria lista. O App também a tem, mas só para o
// contador do menu, e essa vai buscá-la logo no login — antes de haver
// instalação escolhida, e por isso vem vazia.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, ArrowLeft, RefreshCw, Pause } from 'lucide-react';

const API_URL = '/api';
const INTERVALO_LIVE_MS = 2000;

function Cameras({ authHeaders, onBack }) {
  const { t, i18n } = useTranslation();
  const [cameras, setCameras] = useState([]);
  const [snapshots, setSnapshots] = useState({});
  const [aCarregar, setACarregar] = useState({});
  const [modoLive, setModoLive] = useState({});
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState({});

  // Os object URLs têm de ser revogados à mão, senão cada snapshot deixa um
  // blob preso em memória — em modo live seriam 30 por minuto e por câmara.
  const urlsRef = useRef({});

  const carregarSnapshot = useCallback(async (cameraId, mostrarProgresso = true) => {
    if (mostrarProgresso) setACarregar((prev) => ({ ...prev, [cameraId]: true }));
    try {
      const response = await fetch(`${API_URL}/cameras/${cameraId}/snapshot?t=${Date.now()}`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        const url = URL.createObjectURL(await response.blob());
        if (urlsRef.current[cameraId]) URL.revokeObjectURL(urlsRef.current[cameraId]);
        urlsRef.current[cameraId] = url;
        setSnapshots((prev) => ({ ...prev, [cameraId]: url }));
        setUltimaAtualizacao((prev) => ({ ...prev, [cameraId]: new Date() }));
      }
    } catch (err) {
      // Uma captura falhada não vale um erro no ecrã: a próxima tentativa
      // está a dois segundos de distância em modo live, ou a um clique.
    } finally {
      setACarregar((prev) => ({ ...prev, [cameraId]: false }));
    }
  }, [authHeaders]);

  const carregarCameras = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/cameras`, { headers: authHeaders() });
      const data = await response.json();
      if (data.cameras && Array.isArray(data.cameras)) setCameras(data.cameras);
    } catch (err) {
      // Sem lista não há nada a mostrar; o botão de atualizar volta a tentar.
    }
  }, [authHeaders]);

  useEffect(() => { carregarCameras(); }, [carregarCameras]);

  // Primeira captura de todas as câmaras, assim que a lista chega
  useEffect(() => {
    cameras.forEach((cam) => carregarSnapshot(cam.id, true));
  }, [cameras, carregarSnapshot]);

  // Modo live: um intervalo por câmara ativa
  useEffect(() => {
    const intervalos = cameras
      .filter((cam) => modoLive[cam.id])
      .map((cam) => setInterval(() => carregarSnapshot(cam.id, false), INTERVALO_LIVE_MS));
    return () => intervalos.forEach(clearInterval);
  }, [cameras, modoLive, carregarSnapshot]);

  // Ao sair da vista, libertar todos os blobs que ficaram por revogar
  useEffect(() => {
    const urls = urlsRef.current;
    return () => Object.values(urls).forEach(URL.revokeObjectURL);
  }, []);

  const alternarLive = (cameraId) => setModoLive((prev) => ({ ...prev, [cameraId]: !prev[cameraId] }));

  const descarregar = (camera) => {
    const link = document.createElement('a');
    link.href = snapshots[camera.id];
    link.download = `${camera.name}-${new Date().toISOString()}.jpg`;
    link.click();
  };

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <button className="back-button" onClick={onBack}>
            <ArrowLeft size={20} />
            {t('comum.voltar')}
          </button>
          <div className="header-left">
            <Camera size={32} />
            <div>
              <h1>{t('camaras.titulo')}</h1>
              <p>{t('camaras.subtitulo')}</p>
            </div>
          </div>
          <button
            onClick={carregarCameras}
            className="btn-refresh"
          >
            <RefreshCw size={16} />
            {t('comum.atualizar')}
          </button>
        </div>

        {cameras.length === 0 ? (
          <div className="loading-state">
            <RefreshCw size={64} className="spin" />
            <p>{t('camaras.aCarregar')}</p>
          </div>
        ) : (
          <div className="cameras-grid">
            {cameras.map((camera) => {
              const live = modoLive[camera.id];
              const ocupada = aCarregar[camera.id];
              const quando = ultimaAtualizacao[camera.id];

              return (
                <div key={camera.id} className={`camera-card ${live ? 'live-mode' : ''}`}>
                  <div className="camera-header">
                    <h3>{camera.name}</h3>
                    <span className={`camera-status ${live ? 'live' : ''}`}>
                      {live ? `🔴 ${t('camaras.live')}` : `● ${t('comum.online')}`}
                    </span>
                  </div>

                  <div className="camera-preview">
                    {snapshots[camera.id] ? (
                      <div className="camera-image-container">
                        <img
                          src={snapshots[camera.id]}
                          alt={camera.name}
                          className="camera-image"
                          onClick={() => carregarSnapshot(camera.id, true)}
                        />
                        {ocupada && (
                          <div className="camera-loading-overlay">
                            <RefreshCw size={24} className="spin" />
                          </div>
                        )}
                        {quando && (
                          <div className="camera-timestamp">
                            {quando.toLocaleTimeString(i18n.language === 'en' ? 'en-GB' : 'pt-PT')}
                          </div>
                        )}
                      </div>
                    ) : ocupada ? (
                      <>
                        <RefreshCw size={48} className="spin" />
                        <p>{t('camaras.aCapturar')}</p>
                      </>
                    ) : (
                      <>
                        <Camera size={48} />
                        <p>{t('camaras.cliqueCapturar')}</p>
                      </>
                    )}
                  </div>

                  <div className="camera-actions">
                    <button
                      className={`camera-btn ${live ? 'live-active' : ''}`}
                      onClick={() => alternarLive(camera.id)}
                    >
                      {live ? (
                        <><Pause size={16} /> {t('camaras.pararLive')}</>
                      ) : (
                        <><Camera size={16} /> {t('camaras.modoLive')}</>
                      )}
                    </button>

                    <button
                      className="camera-btn"
                      onClick={() => carregarSnapshot(camera.id, true)}
                      disabled={ocupada}
                    >
                      <RefreshCw size={16} />
                      {ocupada ? t('camaras.aCapturarCurto') : t('camaras.capturar')}
                    </button>

                    {snapshots[camera.id] && (
                      <button className="camera-btn" onClick={() => descarregar(camera)}>
                        💾 {t('camaras.descarregar')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Cameras;
